import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCompanyActivity } from '../../services/company_services';
import BoardroomActivityCard from '../../components/BoardroomActivityCard';
import { Box, Typography, useMediaQuery } from '@mui/material';
import LoadingIndicatorLogo from '../../components/LoadingIndicatorLogo';
import ACSnackbar from '../../components/ACSnackbar';
import BoardMeetingCard from '../../components/BoardMeetingsCard';
import BoardUpdateCard from '../../components/BoardUpdateCard';
import ActivityFilter from '../../components/Company/ActivityFilter';

const Activities = React.memo(function Activities({
  company,
  postContainerRef,
  scrollOnFirstLoad,
}) {
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingActivities, setLoadingActivities] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activityFilter, setActivityFilter] = useState([
    'postUpdateCreated',
    'advisorJoinedToCompany',
    'surveyCreated',
    'advisorIdea',
    'boardMeetingCreated',
  ]);
  const limit = 10;
  const containerRef = useRef(null);

  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(isLoadingActivities);
  const filterRef = useRef(activityFilter);
  const offsetRef = useRef(offset);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    loadingRef.current = isLoadingActivities;
  }, [isLoadingActivities]);
  useEffect(() => {
    filterRef.current = activityFilter;
  }, [activityFilter]);
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const fetchItems = useCallback(
    async (nextOffset = offsetRef.current) => {
      if (loadingRef.current || (!hasMoreRef.current && nextOffset > 0)) return;

      setLoadingActivities(true);
      try {
        const f = filterRef.current;
        const filterType =
          f == null || f === 'all' ? null : Array.isArray(f) ? f : [f];

        const response = await getCompanyActivity({
          companyId: company.id,
          limit,
          offset: nextOffset,
          filterType,
        });

        const page = response?.data?.getCompanyActivity ?? [];

        setItems((prev) => (nextOffset === 0 ? page : [...prev, ...page]));
        setHasMore(page.length > 0);
        setOffset(nextOffset + limit);

        if (nextOffset === 0 && scrollOnFirstLoad) {
          if (page.length > 0) {
            postContainerRef?.current?.scrollIntoView({ behavior: 'smooth' });
          } else {
            setIsSnackbarVisible(true);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingActivities(false);
      }
    },
    [company.id, limit, postContainerRef, scrollOnFirstLoad],
  );

  useEffect(() => {
    fetchItems(0);
  }, [fetchItems]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const clientHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;

      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        hasMoreRef.current &&
        !loadingRef.current
      ) {
        fetchItems(offsetRef.current);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fetchItems]);

  const handleFilterChange = (val) => {
    setActivityFilter(val);
    setItems([]);
    setHasMore(true);
    setOffset(0);
    offsetRef.current = 0;
    filterRef.current = val;

    // if (containerRef.current) {
    //   containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    // }

    fetchItems(0);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: items.length > 0 ? 'calc(100vh - 50px)' : '0px',
        overflowY: 'auto',
        backgroundColor: '#f4f8ff',
        maxWidth: isSM ? 'calc(100vw - 30px)' : 'calc(100vw - 80px)',
      }}
    >
      <ACSnackbar
        onClose={() => setIsSnackbarVisible(false)}
        open={isSnackbarVisible}
        severity="warning"
        text="This company has not scheduled any board Activities yet."
      />

      <Typography
        sx={{
          mt: '20px',
          color: '#001030',
          fontWeight: 600,
          fontSize: '20px',
          lineHeight: '130%',
        }}
      >
        Boardroom Activity
      </Typography>
      <Typography
        sx={{
          mt: '10px',
          mb: '20px',
          color: '#4D586F',
          fontWeight: 400,
          fontSize: '14px',
          fontStyle: 'italic',
          lineHeight: '150%',
        }}
      >
        View updates, ideas, introductions and more from the{' '}
        {company.displayName} Advisory Board
      </Typography>

      <ActivityFilter
        value={activityFilter}
        onChange={handleFilterChange}
        size="small"
        fullWidth
        sx={{ mb: '8px' }}
        companyIsP2P={company.boardType == 'P2P'}
      />

      {items.map((item) => {
        if (item.activityType === 'boardMeetingCreated') {
          return (
            <Box sx={{ mb: '30px' }} key={`board-meeting-${item.id}`}>
              <BoardMeetingCard
                boardMeeting={item.boardMeeting}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                company={company}
              />
            </Box>
          );
        }
        if (item.activityType === 'postUpdateCreated') {
          return (
            <Box sx={{ mb: '30px' }} key={`post-update-${item.id}`}>
              <BoardUpdateCard activity={item} company={company} />
            </Box>
          );
        }
        return (
          <BoardroomActivityCard
            key={item.id}
            activity={item}
            company={company}
          />
        );
      })}

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          <LoadingIndicatorLogo size={200} iconFontSize={93} iconRight={105} />
        </Box>
      )}

      {isLoadingActivities && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '180px',
            mb: offset == 0 ? '500px' : '0px',
          }}
        >
          <LoadingIndicatorLogo size={100} iconFontSize={40} iconRight={46} />
        </Box>
      )}
    </Box>
  );
});

export default Activities;
